import { AxiosError } from 'axios';
import { DateTime } from 'luxon';
import { ChangeEvent, FC, useEffect, useState } from 'react';
import Card from '../../../../components/Card/Card';
import Input from '../../../../components/Input/Input';
import { InputStates } from '../../../../components/Input/Input.types';
import { showToast } from '../../../../components/Toast/Toast';
import { ToastTypes } from '../../../../components/Toast/Toast.types';
import Wizard from '../../../../components/Wizard/Wizard';
import { useGlobalFreeze, useUpsertGlobalFreeze } from '../../../../services/freeze/freeze.service';
import useConfirmationModal from '../../../../utils/useConfirmationModal/useConfirmationModal';
import styles from './GlobalFreezeForm.module.css';
import { useTranslation } from 'react-i18next';

interface GlobalFreezeFormProps {
  freezeId?: string;
  isOpen: boolean;
  handleClose: () => void;
}

const GlobalFreezeForm: FC<GlobalFreezeFormProps> = ({ freezeId, isOpen, handleClose }) => {
  const { t } = useTranslation();
  const { data } = useGlobalFreeze(freezeId !== undefined, freezeId!);
  const { mutate, isLoading } = useUpsertGlobalFreeze(freezeId);
  const { open: openConfirmationModal, setTitle } = useConfirmationModal();

  const [formValues, setFormValues] = useState({ name: '', startDate: '', endDate: '', startTime: '', endTime: '' });
  const [formErrors, setFormErrors] = useState({ name: '', startDate: '', endDate: '', startTime: '', endTime: '' });

  const closeAndResetForm = () => {
    setFormValues({ name: '', startDate: '', endDate: '', startTime: '', endTime: '' });
    setFormErrors({ name: '', startDate: '', endDate: '', startTime: '', endTime: '' });
    handleClose();
  };

  useEffect(() => {
    if (data?.data) {
      setFormValues({
        name: data?.data.name,
        startDate: DateTime.fromISO(data.data.startDate).toISODate() || '',
        endDate: DateTime.fromISO(data.data.endDate).toISODate() || '',
        startTime: DateTime.fromISO(data.data.startDate).toFormat('TT') || '',
        endTime: DateTime.fromISO(data.data.endDate).toFormat('TT') || '',
      });
    }
  }, [data?.data]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (formErrors[event.target.id as keyof typeof formErrors])
      setFormErrors((prevFormErrors) => ({ ...prevFormErrors, [event.target.id]: '' }));
    setFormValues((prevFormValues) => ({ ...prevFormValues, [event.target.id]: event.target.value }));
  };

  const handleNextClick = () => {
    const errors = { name: '', startDate: '', endDate: '', startTime: '', endTime: '' };
    if (formValues.startDate < DateTime.now().toISODate()) errors.startDate = t('error.startDateInPast');
    if (formValues.name === '') errors.name = t('error.validName');
    if (
      formValues.startDate === DateTime.now().toISODate() &&
      formValues.startTime < DateTime.now().toLocaleString(DateTime.TIME_24_SIMPLE)
    )
      errors.startTime = t('error.startDateInPast');
    if (formValues.startDate === '') errors.startDate = t('error.validStartDate');
    if (formValues.endDate === '') errors.endDate = t('error.validEndDate');
    if (formValues.endDate < formValues.startDate) errors.endDate = t('error.freezeEndDate');
    if (formValues.endDate === formValues.startDate && formValues.endTime < formValues.startTime)
      errors.endTime = t('error.freezeEndDate');
    if (formValues.startTime === '') errors.startTime = t('error.validStartTime');
    if (formValues.endTime === '') errors.endTime = t('error.validEndTime');
    if (errors.name || errors.startDate || errors.endDate || errors.startTime || errors.endTime) {
      setFormErrors(errors);
      return;
    }
    mutate(
      {
        name: formValues.name,
        startDate: DateTime.fromISO(`${formValues.startDate}T${formValues.startTime}`).toISO() ?? '',
        endDate: DateTime.fromISO(`${formValues.endDate}T${formValues.endTime}`).toISO() ?? '',
      },
      {
        onSuccess: () => {
          setTitle(
            t(`toolkitFreeze.createModal.${freezeId ? 'updated' : 'created'}Successfully`, {
              formName: formValues.name,
            }),
          );
          openConfirmationModal();
          closeAndResetForm();
        },
        onError: (error) => {
          showToast({
            toastType: ToastTypes.Error,
            label: (error as AxiosError).message || (freezeId ? t('error.updatingFreeze') : t('error.creatingFreeze')),
            id: 'upsertGlobalFreezeError',
          });
        },
      },
    );
  };

  return (
    <Wizard
      showStepIndicator={false}
      isOpen={isOpen}
      handleClose={closeAndResetForm}
      steps={[
        {
          title: freezeId ? t('toolkitFreeze.createModal.titleUpdate') : t('toolkitFreeze.createModal.titleCreate'),
          description: t('toolkitFreeze.createModal.description'),
          onBackClick: closeAndResetForm,
          nextButtonText: t('common.confirm'),
          onNextClick: handleNextClick,
          nextButtonIsLoading: isLoading,
          content: (
            <Card className={styles.editFreezeCard}>
              <div className={styles.inputsWrapper}>
                <Input
                  className={styles.nameInput}
                  label={t('toolkitFreeze.createModal.freezeName')}
                  id="name"
                  value={formValues.name}
                  onChange={handleChange}
                  subText={formErrors.name}
                  state={formErrors.name ? InputStates.Error : undefined}
                />
                <div className={styles.dateAndTime}>
                  <Input
                    label={t('toolkitFreeze.createModal.freezeStartDate')}
                    id="startDate"
                    value={formValues.startDate}
                    onChange={handleChange}
                    type="date"
                    subText={formErrors.startDate}
                    state={formErrors.startDate ? InputStates.Error : undefined}
                    min={DateTime.now().toISODate()}
                  />
                  <Input
                    label={t('toolkitFreeze.createModal.startTime')}
                    id="startTime"
                    value={formValues.startTime}
                    onChange={handleChange}
                    type="time"
                    subText={formErrors.startTime}
                    state={formErrors.startTime ? InputStates.Error : undefined}
                  />
                </div>
                <div className={styles.dateAndTime}>
                  <Input
                    label={t('toolkitFreeze.createModal.freezeEndDate')}
                    id="endDate"
                    value={formValues.endDate}
                    subText={formErrors.endDate}
                    onChange={handleChange}
                    type="date"
                    min={formValues.startDate}
                    state={formErrors.endDate ? InputStates.Error : undefined}
                  />
                  <Input
                    label={t('toolkitFreeze.createModal.endTime')}
                    id="endTime"
                    value={formValues.endTime}
                    onChange={handleChange}
                    type="time"
                    subText={formErrors.endTime}
                    state={formErrors.endTime ? InputStates.Error : undefined}
                  />
                </div>
              </div>
            </Card>
          ),
        },
      ]}
    />
  );
};

export default GlobalFreezeForm;
